import React, { useContext, useMemo, useRef, useCallback } from 'react';
import { Checkbox } from '@material-ui/core';
import { PatientChartContext } from '../../../contexts/patientChart';
import { PRODUCT_INFO, EMAIL, MODAL_KEY, RESPONSE_POPUP_KEY, CONFIRMATION_POPUP_KEY } from '../../../constants/patientChart.constant';
import { EmailReason } from '../../../constants/visits.constant';
import { FEATURE } from '../../../utils/feature';
import { POPUP_TYPE } from '../../subComponents/popup';
import PPButton from '../../subComponents/button';
import Input from '../../subComponents/input';
import Select from '../../subComponents/select';
import Editor, { pickEditor, pickEditorState } from '../../subComponents/editor/jodit/v2';

const hasFeature = FEATURE();

const JoditEditorId = 'jodit-editor-email-message';

export const EmailPatient = ({
  orderId = '',
  patientId = '',
  product = PRODUCT_INFO(),
  emailReasons: reasons = {},
  emailTemplatesV2: templates = {},
  sendEmailInfo = {},
  sendEmail = async () => {},
  getEmails = async () => {},
}) => {
  const { loading } = sendEmailInfo.api;
  const {
    email,
    setEmail,
    handleModalOpen,
    handleMultipleModalClose,
    joditEditorTemplateForKit,
    setJoditEditorTemplate,
    setJoditEditorTemplateForKit,
  } = useContext(PatientChartContext);
  const editorRef = useRef(new Map());

  const emailCollection = useMemo(() => {
    const collection = new Map();
    for (let i = 0; i < templates.data.length; i++) {
      const template = templates.data[i];
      collection.set(template.value, template);
    }
    return collection;
  }, [templates.data]);

  const handleTemplateChange = useCallback(({ updatedState }) => {
    setJoditEditorTemplateForKit(updatedState);
  }, []);

  const joditConfig = useMemo(() => ({
    id: JoditEditorId,
    name: JoditEditorId,
    className: JoditEditorId,
    editorRef: editorRef,
    value: '',
    extras: {
      key: JoditEditorId,
      kitPId: 0,
    },
    onChange: handleTemplateChange,
  }), [handleTemplateChange]);

  const handleChange = ({ target }) => {
    const template = emailCollection.get(target.value) || { label: '' };
    const subject = templates.subjects.data.get(target.value) || { EmailTemplateBody: '', EmailTemplateSubject: '' };
    setEmail({ ...email, value: target.value, template, subject });

    if (hasFeature.RICH_TEXT_EDITOR) {
      try {
        const editor = pickEditor({
          name: joditConfig.name,
          editorCollection: joditConfig.editorRef.current,
        });
        editor.value = subject?.EmailTemplateBody || '';
      } catch(e) {
  
      }
    }
  };

  const handleSubjectChange = ({ target }) => {
    setEmail({ ...email, subject: { ...email.subject, EmailTemplateSubject: target.value } });
  };

  const handleMessageChange = useCallback(({ target }) => {
    setEmail({ ...email, subject: { ...email.subject, EmailTemplateBody: target.value } });

    try {
      const editor = pickEditor({
        name: joditConfig.name,
        editorCollection: joditConfig.editorRef.current,
      });
      editor.value = target.value;
    } catch(e) {

    }
  }, [email]);

  const handleJoditEditorChange = useCallback((_jodit, _extras = {}, _event) => {
    const editorTemplate = {
      text: _jodit?.text || '',
      value: _jodit?.value || '',
    };
    setJoditEditorTemplate(editorTemplate);
  }, []);

  const handleReasonChange = ({ target }) => {
    const collection = new Map(reasons.valuesCollection);
    const reason = collection.get(target.value) || EmailReason();
    setEmail({ ...email, reason });
  };

  const handleReasonDescriptionChange = ({ target }) => {
    setEmail({ ...email, reason: { ...email.reason, description: target.value } });
  };

  const handleSendTextToPatientChange = (_, sendTextToPatient = false) => {
    setEmail({ ...email, sendTextToPatient });
  };

  const handleSendEmail = async ({ key }) => {
    const body = {
      EmailMessage: email.subject.EmailTemplateBody,
      EmailSubject: email.subject.EmailTemplateSubject,
      OrderId: orderId,
      PatientId: patientId,
      EmailReasonTemplateId: email.reason.value,
      OrderEmailReason: email.reason.description,
      SentTextWithEmail: email.sendTextToPatient,
      Storefront: product.StoreFrontData.WorkingName,
    };


    if (hasFeature.RICH_TEXT_EDITOR) {
      const editorState = pickEditorState({
        name: JoditEditorId,
        state: joditEditorTemplateForKit,
      });
      body.EmailMessage = editorState?.value || '';
    }

    try {
      const { status, data } = await sendEmail(body);
      if (status === 200) {
        if (hasFeature.IS_SALESFORCE_CONFIGURED) {
          getEmails({ OrderId: orderId });
        }

        setEmail(EMAIL());
        handleMultipleModalClose.call(this, { keys: [key, MODAL_KEY.EMAIL_PATIENT] });
      }

      if (status !== 200) {
        const { Error: error = 'Failed to send email. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.EMAIL_PATIENT, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {

    }
  };

  const { message, joditPureText } = useMemo(() => {
    if (!hasFeature.RICH_TEXT_EDITOR) {
      if (!email.subject?.EmailTemplateBody) {
        return { message: '', joditPureText: '' };
      }

      return {
        message: email.subject.EmailTemplateBody.replace(/(<br>)/gmi, '\n'),
        joditPureText: '',
      };
    }

    const editorState = pickEditorState({
      name: JoditEditorId,
      state: joditEditorTemplateForKit,
    });
    if (!editorState?.text) {
      return { message: '', joditPureText: '' };
    }

    return {
      message: editorState.value,
      joditPureText: editorState.text,
    };
  }, [email, joditEditorTemplateForKit]);

  const EmailInputCollection = useMemo(() => new Map([
    [false, () => (
      <textarea
        name='emailMessage'
        className='flex flex-fill border-light-grey-1 min-height-160 height-full width-full p-4 font-proxima-nova font-14 color-dark-grey border-box resize-none'
        wrap='soft'
        maxLength={65535}
        value={message}
        onChange={handleMessageChange}
      />
    )],
    [true, () => (
      <Editor
        key={joditConfig.id}
        id={joditConfig.id}
        name={joditConfig.name}
        className={joditConfig.className}
        editorRef={joditConfig.editorRef}
        value={joditConfig.value}
        extras={joditConfig.extras}
        onChange={joditConfig.onChange}
      />
    )],
  ]), [message, handleMessageChange, handleJoditEditorChange]);

  const EmailInput = useMemo(() => EmailInputCollection.get(hasFeature.RICH_TEXT_EDITOR), [EmailInputCollection]);

  const Template = (
    <div className='flex flex-column flex-fill m-b-5'>
      <div className='flex align-items-center m-b-5'>
        <label className="input-label width-110 m-r-4">Email Template:</label>
        <Select
          id='templateId'
          labelId="templateId"
          value={email.value}
          onChange={handleChange}
          items={templates.data}
        />
      </div>

      <div className='flex align-items-center m-b-5'>
        <Input
          name='emailSubject'
          type='text'
          label='Subject Line:'
          className='flex flex-fill'
          labelClassName='width-110'
          max={255}
          value={email.subject.EmailTemplateSubject}
          onChange={handleSubjectChange}
        />
      </div>

      <div className='flex flex-fill flex-column gap-12 align-items-flex-start m-b-5'>
        <label className="input-label">Email Message:</label>
        <EmailInput />
      </div>

      <div className='flex align-items-center m-b-5'>
        <label className="input-label width-110 m-r-4">Email Reason:</label>
        <Select
          id='reasonId'
          labelId="reasonId"
          value={email.reason.value}
          onChange={handleReasonChange}
          items={reasons.data}
        />
      </div>

      {
        email.reason.required && (
          <div className='flex align-items-center m-b-5'>
            <div className="width-110 m-r-4"></div>
            <textarea
              name='emailReason'
              className='flex flex-fill border-light-grey-1 min-height-100 p-4 font-proxima-nova font-14 color-dark-grey border-box resize-none'
              wrap='soft'
              maxLength={255} // db can support 5000
              value={email.reason.description}
              onChange={handleReasonDescriptionChange}
            />
          </div>
        )
      }

      <div className='flex align-items-center'>
        <label className="input-label width-110 m-r-4">Text Patient:</label>
        <Checkbox
          title="sendTextToPatient"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={email.sendTextToPatient}
          onChange={handleSendTextToPatientChange}
        />
      </div>
    </div>
  );

  const _disabled = useMemo(() => {
    if (!email.subject.EmailTemplateSubject) {
      return true;
    }

    if (!email.reason.description) {
      return true;
    }

    if (!hasFeature.RICH_TEXT_EDITOR) {
      return !message;
    }

    return !joditPureText;
  }, [email.subject, email.reason, message, joditPureText]);

  const Actions = (
    <div className='flex justify-content-flex-end'>
      <PPButton label='Email Patient' variant='contained' loading={loading} disabled={_disabled} endGutter onClick={handleSendEmail.bind(this, { key: CONFIRMATION_POPUP_KEY.EMAIL_PATIENT, modal: {} })} />
      <PPButton label='Cancel' variant='cancel' disabled={loading} onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.EMAIL_PATIENT] })} />
    </div>
  );

  return (
    <div className='flex flex-column flex-fill m-5 patient-chart-container'>
      {Template}
      {Actions}
    </div>
  );
};

export default EmailPatient;
